import React, { forwardRef, useMemo } from 'react';

const DEFAULT_PRIMARY = '#273C89';

function hexToRgb(hex) {
	const value = String(hex).replace('#', '').trim();
	const normalized =
		value.length === 3
			? value
					.split('')
					.map((c) => c + c)
					.join('')
			: value;

	if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;

	return {
		r: parseInt(normalized.slice(0, 2), 16),
		g: parseInt(normalized.slice(2, 4), 16),
		b: parseInt(normalized.slice(4, 6), 16),
	};
}

/**
 * Darkens the supplied primary color by the requested percentage.
 * structureDarkness=0.2 means 20% darker.
 */
function darkenColor(color, amount = 0.2) {
	const rgb = hexToRgb(color);
	if (!rgb) return color;

	const factor = Math.max(0, 1 - amount);
	return `rgb(${Math.round(rgb.r * factor)}, ${Math.round(rgb.g * factor)}, ${Math.round(rgb.b * factor)})`;
}

const MobileSimSvg = forwardRef(function MobileSimSvg(
	{
		primaryColor = DEFAULT_PRIMARY,
		upperOpacity = 0.7,
		structureDarkness = 0.2,
		className,
		style,
		...rest
	},
	ref,
) {
	const darkPrimaryColor = useMemo(
		() => darkenColor(primaryColor, structureDarkness),
		[primaryColor, structureDarkness],
	);

	return (
		<svg
			ref={ref}
			width="386"
			height="257"
			viewBox="0 0 386 257"
			preserveAspectRatio="xMidYMid slice"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
			aria-hidden="true"
			style={{
				display: 'block',
				width: '100%',
				height: 'auto',
				'--primary-color': primaryColor,
				'--dark-primary-color': darkPrimaryColor,
				'--upper-opacity': upperOpacity,
				...style,
			}}
			{...rest}
		>
			<g clipPath="url(#clip0_3850_4208)">
				<path
					d="M0 0H386V128.502V257H0V128.539V0Z"
					fill="var(--primary-color)"
				/>
				<path
					d="M0 0H386V128.502C351.408 128.85 316.47 128.089 281.89 128.536C281.898 128.362 281.902 128.189 281.905 128.016L281.87 113.397C281.9 108.469 282.212 103.153 281.362 98.3138C280.835 95.3027 277.652 91.6685 274.5 91.3672C268.383 90.7827 261.973 91.032 255.792 91.0332L216.086 91.0275C216.19 81.8392 216.213 72.6503 216.154 63.4618C216.173 57.855 216.471 51.1755 215.794 45.634C215.583 43.9055 213.85 40.6287 212.544 39.4268C208.875 36.048 205.884 35.3108 201.115 35.2998C184.914 35.2625 168.737 35.3472 152.556 35.3242L136.005 35.305C129.624 35.3042 123.281 34.4005 118.485 39.2495C113.552 44.2373 114.276 51.2685 114.263 57.6577L114.26 73.6423L114.225 128.506L36.4365 128.503C24.4213 128.497 11.9845 128.332 0 128.539V0Z"
					fill="var(--primary-color)"
					fillOpacity={{ upperOpacity }}
				/>
				<path
					d="M209.514 169.989L209.545 189.477C209.549 192.498 209.788 196.6 209.296 199.462C208.629 203.342 204.056 206.485 200.225 206.499C195.136 206.518 190.142 206.535 185.096 206.532L155.98 206.517L138.703 206.531C136.47 206.539 131.176 206.678 129.229 206.428C127.277 206.186 125.449 205.339 124.003 204.006C121.782 201.956 121.246 199.754 121.214 196.795C121.103 186.767 121.138 176.664 121.133 166.639L121.167 106.809L121.145 69.0152C121.143 62.6612 120.961 56.0245 121.293 49.775C121.601 43.9815 126.652 41.736 131.664 41.7565C147.308 41.8202 163.108 41.7467 178.724 41.7762C183.449 41.7912 188.175 41.7795 192.9 41.7407C197.514 41.7155 203.188 40.9122 206.695 44.371C210.046 47.6775 209.503 50.5247 209.54 54.84C209.56 57.2835 209.539 59.744 209.538 62.1897L209.518 91.0202L179.576 90.999C174.842 90.998 170.174 90.9717 165.44 91.1337C160.965 91.287 156.553 94.5537 156.558 99.2975C156.272 103.342 156.387 108.77 156.386 112.903L156.388 135.796L156.367 152.357C156.367 154.865 156.25 159.441 156.547 161.74C156.46 165.372 160.062 169.218 163.43 169.626C168.415 170.231 174.266 169.986 179.294 169.98L209.514 169.989Z"
					fill="#FBF3E8"
				/>
				<rect
					x="152.68"
					y="47.4609"
					width="25"
					height="4"
					rx="2"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="152.68"
					y="47.4609"
					width="25"
					height="4"
					rx="2"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="152.645"
					y="196.889"
					width="25"
					height="4"
					rx="2"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="152.645"
					y="196.889"
					width="25"
					height="4"
					rx="2"
					fill="white"
					fillOpacity="0.571788"
				/>
				<path
					d="M165.863 97.3716C166.485 97.1165 167.157 97.015 167.83 96.9958C167.901 96.9938 167.972 96.9919 168.043 96.9902C175.795 96.8047 184.2 96.9579 191.986 96.9579H237.69H260.182C263.82 96.9557 267.863 96.7982 271.465 97.1427C272.222 97.3188 272.96 97.7285 273.639 98.218C274.916 99.1387 275.533 100.698 275.563 102.272C275.671 107.905 275.585 113.787 275.585 119.364C275.637 127.493 275.624 135.623 275.549 143.752C275.534 145.393 274.739 146.958 273.536 148.075C270.58 150.819 265.694 155.808 264.01 157.422C262.458 158.893 260.01 161.603 258.388 162.792L258.243 162.897C256.168 164.419 240.749 163.895 237.217 163.889L207.641 163.84L183.211 163.832C179.491 163.84 170.981 164.229 166.466 163.466C165.313 163.271 164.279 162.626 163.564 161.7C162.791 160.699 162.488 159.432 162.456 158.168C162.061 142.891 162.602 127.463 162.351 112.187C162.302 109.191 162.236 105.988 162.372 102.915C162.446 101.223 162.959 99.4876 164.227 98.3642C164.683 97.9607 165.182 97.6507 165.863 97.3716Z"
					fill="#FBF3E8"
				/>
				<path
					d="M187.024 128.685L184.997 121.928C184.612 120.645 185.573 119.354 186.913 119.354H193.906C195.283 119.354 196.248 120.714 195.793 122.014L193.439 128.74C193.3 129.139 193.29 129.571 193.411 129.976L195.452 136.779C195.837 138.062 194.876 139.354 193.537 139.354H186.179C184.764 139.354 183.796 137.924 184.322 136.611L186.965 130.002C187.133 129.582 187.154 129.118 187.024 128.685Z"
					fill="var(--dark-primary-color)"
				/>
				<path
					d="M187.024 128.685L184.997 121.928C184.612 120.645 185.573 119.354 186.913 119.354H193.906C195.283 119.354 196.248 120.714 195.793 122.014L193.439 128.74C193.3 129.139 193.29 129.571 193.411 129.976L195.452 136.779C195.837 138.062 194.876 139.354 193.537 139.354H186.179C184.764 139.354 183.796 137.924 184.322 136.611L186.965 130.002C187.133 129.582 187.154 129.118 187.024 128.685Z"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="174.902"
					y="126.93"
					width="8"
					height="5"
					rx="1"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="174.902"
					y="126.93"
					width="8"
					height="5"
					rx="1"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="197.387"
					y="126.877"
					width="8"
					height="5"
					rx="1"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="197.387"
					y="126.877"
					width="8"
					height="5"
					rx="1"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="199.916"
					y="119.473"
					width="5"
					height="3"
					rx="1.5"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="199.916"
					y="119.473"
					width="5"
					height="3"
					rx="1.5"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="199.814"
					y="136.135"
					width="5"
					height="3"
					rx="1.5"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="199.814"
					y="136.135"
					width="5"
					height="3"
					rx="1.5"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="175.002"
					y="136.111"
					width="5"
					height="3"
					rx="1.5"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="175.002"
					y="136.111"
					width="5"
					height="3"
					rx="1.5"
					fill="white"
					fillOpacity="0.571788"
				/>
				<rect
					x="175.029"
					y="119.463"
					width="5"
					height="3"
					rx="1.5"
					fill="var(--dark-primary-color)"
				/>
				<rect
					x="175.029"
					y="119.463"
					width="5"
					height="3"
					rx="1.5"
					fill="white"
					fillOpacity="0.571788"
				/>
			</g>
		</svg>
	);
});

export default MobileSimSvg;
